import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useUserCredits } from '@/hooks/useUserCredits';
import { getAuthHeaders } from '@/lib/authHeaders';
import { useEventTracking } from '@/hooks/useEventTracking';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const copy = {
  en: {
    label: 'Refine tone',
    warmer: 'Warmer',
    confident: 'More confident',
    shorter: 'Shorter',
    removeAwkward: 'Smoother',
    applying: 'Refining…',
    applied: 'Tone adjusted!',
    failed: 'Could not refine. Try again.',
    noText: 'Write something first, then refine.',
    limitReached: 'Refine limit reached for this session.',
  },
  es: {
    label: 'Refinar tono',
    warmer: 'Más cálido',
    confident: 'Más seguro',
    shorter: 'Más corto',
    removeAwkward: 'Más fluido',
    applying: 'Refinando…',
    applied: '¡Tono ajustado!',
    failed: 'No se pudo refinar. Inténtalo de nuevo.',
    noText: 'Escribe algo primero, luego refina.',
    limitReached: 'Límite de refinamiento alcanzado.',
  },
  fr: {
    label: 'Affiner le ton',
    warmer: 'Plus chaleureux',
    confident: 'Plus assuré',
    shorter: 'Plus court',
    removeAwkward: 'Plus fluide',
    applying: 'Affinage…',
    applied: 'Ton ajusté !',
    failed: 'Échec du raffinement. Réessayez.',
    noText: "Écrivez quelque chose d'abord.",
    limitReached: 'Limite de raffinement atteinte.',
  },
  pt: {
    label: 'Refinar tom',
    warmer: 'Mais caloroso',
    confident: 'Mais confiante',
    shorter: 'Mais curto',
    removeAwkward: 'Mais fluido',
    applying: 'Refinando…',
    applied: 'Tom ajustado!',
    failed: 'Não foi possível refinar. Tente novamente.',
    noText: 'Escreva algo primeiro.',
    limitReached: 'Limite de refinamento atingido.',
  },
  ar: {
    label: 'تحسين النبرة',
    warmer: 'أكثر دفئاً',
    confident: 'أكثر ثقة',
    shorter: 'أقصر',
    removeAwkward: 'أكثر سلاسة',
    applying: 'جارٍ التحسين…',
    applied: 'تم تعديل النبرة!',
    failed: 'فشل التحسين. حاول مرة أخرى.',
    noText: 'اكتب شيئاً أولاً.',
    limitReached: 'تم الوصول إلى حد التحسين.',
  },
  ja: {
    label: 'トーンを調整',
    warmer: 'より温かく',
    confident: 'より自信をもって',
    shorter: '短く',
    removeAwkward: 'より滑らかに',
    applying: '調整中…',
    applied: 'トーンを調整しました！',
    failed: '調整に失敗しました。もう一度お試しください。',
    noText: 'まず何か書いてください。',
    limitReached: '調整回数の上限に達しました。',
  },
  ko: {
    label: '톤 조정',
    warmer: '더 따뜻하게',
    confident: '더 자신있게',
    shorter: '더 짧게',
    removeAwkward: '더 부드럽게',
    applying: '조정 중…',
    applied: '톤이 조정되었습니다!',
    failed: '조정에 실패했습니다. 다시 시도하세요.',
    noText: '먼저 무언가를 작성하세요.',
    limitReached: '조정 횟수 제한에 도달했습니다.',
  },
  id: {
    label: 'Sesuaikan nada',
    warmer: 'Lebih hangat',
    confident: 'Lebih percaya diri',
    shorter: 'Lebih pendek',
    removeAwkward: 'Lebih halus',
    applying: 'Menyesuaikan…',
    applied: 'Nada disesuaikan!',
    failed: 'Gagal menyesuaikan. Coba lagi.',
    noText: 'Tulis sesuatu terlebih dahulu.',
    limitReached: 'Batas penyesuaian tercapai.',
  },

  hi: {
    label: 'स्वर परिष्कृत करें',
    warmer: 'अधिक गर्मजोशी भरा',
    confident: 'अधिक आत्मविश्वासपूर्ण',
    shorter: 'छोटा',
    removeAwkward: 'अधिक सहज',
    applying: 'परिष्कृत हो रहा है…',
    applied: 'स्वर समायोजित हो गया!',
    failed: 'परिष्कृत नहीं हो सका। पुनः प्रयास करें।',
    noText: 'पहले कुछ लिखें, फिर परिष्कृत करें।',
    limitReached: 'इस सत्र के लिए परिष्करण सीमा पहुँच गई।',
  },
};

interface MessageToneToolsProps {
  message: string;
  recipientName: string;
  relationship: string;
  template: string;
  onMessageUpdate: (newMessage: string) => void;
}

export function MessageToneTools({ message, recipientName, relationship, template, onMessageUpdate }: MessageToneToolsProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const { canUseAiAssist, useAiAssist } = useUserCredits();
  const { trackEvent } = useEventTracking();
  const [isRefining, setIsRefining] = useState(false);
  const [activeRefine, setActiveRefine] = useState<string | null>(null);

  const toneOptions = [
    { key: 'warmer', label: c.warmer, instruction: 'Make the tone warmer, more tender and affectionate. Keep the same content and meaning.' },
    { key: 'confident', label: c.confident, instruction: 'Make the tone more confident and assertive while keeping it sincere. Remove hedging language.' },
    { key: 'shorter', label: c.shorter, instruction: 'Make it shorter and more concise. Keep the core emotion but remove filler and redundancy.' },
    { key: 'removeAwkward', label: c.removeAwkward, instruction: 'Smooth out awkward phrasing. Make the language flow naturally while preserving the voice and meaning.' },
  ];

  const handleRefine = useCallback(async (key: string, instruction: string) => {
    if (!message || message.length < 20) {
      toast.info(c.noText);
      return;
    }

    setIsRefining(true);
    setActiveRefine(key);
    trackEvent('tone_refine_clicked', { tone: key, template, relationship });

    try {
      await useAiAssist();
      const headers = await getAuthHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type: 'jackey_v2_refine',
          recipientName,
          relationship,
          template,
          currentDraft: message,
          refineType: instruction,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) { toast.error(c.failed); return; }
        throw new Error('Refine failed');
      }

      const data = await response.json();
      if (data.message) {
        onMessageUpdate(data.message);
        toast.success(c.applied);
        trackEvent('tone_refine_applied', { tone: key });
      }
    } catch {
      toast.error(c.failed);
    } finally {
      setIsRefining(false);
      setActiveRefine(null);
    }
  }, [message, recipientName, relationship, template, onMessageUpdate, c, trackEvent, useAiAssist]);

  // Only show when there's meaningful text
  if (!message || message.length < 20) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-wrap items-center gap-1.5"
    >
      <Wand2 className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-xs text-muted-foreground mr-1">{c.label}:</span>
      {toneOptions.map(({ key, label, instruction }) => (
        <Button
          key={key}
          type="button"
          variant="outline"
          size="sm"
          disabled={isRefining}
          onClick={() => handleRefine(key, instruction)}
          className="h-7 text-xs px-2.5 rounded-full"
        >
          {isRefining && activeRefine === key ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            label
          )}
        </Button>
      ))}
    </motion.div>
  );
}
